import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShieldCheck, CheckCircle2, Building2, Store, CreditCard,
    ArrowLeft, Copy, Check, Mail, Phone, ExternalLink,
    Sparkles, Layers, Sliders, Server, HardDrive, RefreshCw,
    HelpCircle, ChevronRight, Lock, Clock, Info,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import apiClient from '@/lib/api-client';

interface BankDetails {
    bankName: string;
    accountNumber: string;
    accountName: string;
    proofEmail: string;
    supportPhone: string;
    paymentInstructions: string;
}

interface StandardPlan {
    id: string;
    name: string;
    tagline: string;
    monthlyPrice: number;
    yearlyPrice: number;
    currency: string;
    businessModel: 'single' | 'multi';
    features: string[];
    isPopular: boolean;
}

interface FeatureItem {
    id: string;
    name: string;
    monthlyPrice: number;
    category: string;
    description: string;
}

interface LocalInstallation {
    baseSetupPrice: number;
    description: string;
    withoutBackup: {
        label: string;
        price: number;
        details: string;
    };
    withBackup: {
        label: string;
        setupPrice: number;
        cycles: {
            cycle: 'monthly' | 'bimonthly' | 'quarterly' | 'semiannual' | 'annual';
            label: string;
            price: number;
        }[];
        details: string;
    };
    locationTerms: string[];
}

interface SubscriptionSettingsData {
    bankDetails: BankDetails;
    standardPlans: StandardPlan[];
    featurePricing: FeatureItem[];
    localInstallation: LocalInstallation;
}

export function SubscriptionPackagesPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tenantId = searchParams.get('tenantId') || '';
    const emailParam = searchParams.get('email') || '';

    const [activeTab, setActiveTab] = useState<'standard' | 'custom' | 'local'>('standard');
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
    const [copiedField, setCopiedField] = useState<string | null>(null);

    // Feature calculator state
    const [selectedFeatures, setSelectedFeatures] = useState<string[]>([
        'pos',
        'inventory',
    ]);
    const BASE_PLATFORM_FEE = 8000;

    // Local installation state
    const [localMode, setLocalMode] = useState<'without_backup' | 'with_backup'>('with_backup');
    const [selectedBackupCycle, setSelectedBackupCycle] = useState<string>('quarterly');

    // Fetch subscription package settings from backend (with fallback)
    const { data: settings } = useQuery<SubscriptionSettingsData>({
        queryKey: ['platform-subscription-settings'],
        queryFn: async () => {
            const { data } = await apiClient.get('/platform/subscription-settings');
            return data?.data;
        },
    });

    const bankDetails = settings?.bankDetails || {
        bankName: 'Guaranty Trust Bank (GTBank) / Zenith Bank',
        accountNumber: '0123456789',
        accountName: 'EtiPOS Systems Limited',
        proofEmail: 'billing@etipos.com',
        supportPhone: '+234 802 345 6789',
        paymentInstructions: 'Make direct bank transfer with your registered Company Name or Slug as payment reference, then send screenshot/receipt to proofEmail for instant verification within 15 minutes.',
    };

    const standardPlans = settings?.standardPlans || [
        {
            id: 'single',
            name: 'Single Business Plan',
            tagline: 'Ideal for single retail shops, boutiques, cafes and standalone outlets',
            monthlyPrice: 15000,
            yearlyPrice: 150000,
            currency: 'NGN',
            businessModel: 'single',
            features: [
                '1 Business Location & 1 Main Branch',
                'Up to 3 Active POS Terminals',
                'Unlimited Products & Barcode Printing',
                'Real-Time Stock Inventory & Low-Stock Alerts',
                'Customer Profiles & Loyalty Points',
                'Shift End-of-Day Financial Balancing',
                'Daily Revenue & Sales Reporting',
                'Standard Email & WhatsApp Support',
            ],
            isPopular: false,
        },
        {
            id: 'multi',
            name: 'Multi-Business Enterprise',
            tagline: 'Built for chains, supermarkets, multi-branch operations and enterprise distributors',
            monthlyPrice: 45000,
            yearlyPrice: 450000,
            currency: 'NGN',
            businessModel: 'multi',
            features: [
                'Unlimited Businesses & Unlimited Branches',
                'Inter-Branch Stock Transfers & Consolidated Ledger',
                'BOM & Production Manufacturing Floors',
                'Procurement Requisitions, Purchase Orders & GRN 3-Way Match',
                'Kitchen Display System (KDS)',
                'Granular Role-Based Permissions & Staff Assignment',
                'Gift Hampers & Clearance Promotional Campaigns',
                'Consolidated Multi-Location P&L Analytics',
                'Dedicated Account Manager & Priority 24/7 SLA',
            ],
            isPopular: true,
        },
    ];

    const featurePricing = settings?.featurePricing || [
        { id: 'pos', name: 'POS Terminal & Barcode Scanner Checkout', monthlyPrice: 3000, category: 'Core POS', description: 'Fast barcode scanning, cart management, split payments and thermal receipts.' },
        { id: 'inventory', name: 'Inventory Ledger & Stock Level Tracking', monthlyPrice: 4000, category: 'Inventory', description: 'Perpetual stock ledger, valuation reports, FIFO tracking and discrepancy reconciliation.' },
        { id: 'transfers', name: 'Inter-Branch Stock Transfers & Dispatch', monthlyPrice: 4500, category: 'Logistics', description: 'Inter-warehouse transit, dispatch confirmations and automatic receipt matching.' },
        { id: 'procurement', name: 'Procurement, POs & GRN Matching', monthlyPrice: 4000, category: 'Supply Chain', description: 'Purchase requisitions, vendor RFQs, purchase orders and Goods Received Notes.' },
        { id: 'bom', name: 'BOM & Production Floor Management', monthlyPrice: 8000, category: 'Manufacturing', description: 'Recipe bill of materials, raw material depletion and batch production floor tracking.' },
        { id: 'kds', name: 'Kitchen Display System (KDS)', monthlyPrice: 3500, category: 'Hospitality', description: 'Real-time order routing to kitchen displays with prep timers and fulfillment states.' },
        { id: 'hampers', name: 'Gift Hampers & Clearance Engine', monthlyPrice: 3000, category: 'Marketing', description: 'Holiday gift hampers, bundles and clearance markdown campaigns.' },
        { id: 'banking', name: 'Banking & Expense Ledger Management', monthlyPrice: 3500, category: 'Finance', description: 'Multi-bank accounts, expense approvals, petty cash and deposit reconciliation.' },
        { id: 'analytics', name: 'Advanced Executive Analytics & P&L', monthlyPrice: 5000, category: 'Intelligence', description: 'Consolidated profit margins, ABC product classification and predictive reorder.' },
    ];

    const localInstallation = settings?.localInstallation || {
        baseSetupPrice: 120000,
        description: 'On-premise hardware deployment for air-gapped operations or high-reliability local network environments.',
        withoutBackup: {
            label: 'Local Installation (Without Cloud Backup)',
            price: 120000,
            details: 'Includes on-site server deployment, local PostgreSQL setup, LAN terminal routing, and client-managed local drive backup.',
        },
        withBackup: {
            label: 'Local Installation (With Automated Cloud Backup)',
            setupPrice: 120000,
            cycles: [
                { cycle: 'monthly', label: 'Monthly Cloud Sync', price: 12500 },
                { cycle: 'bimonthly', label: 'Bi-Monthly (Every 2 Months)', price: 23000 },
                { cycle: 'quarterly', label: 'Quarterly Cloud Sync', price: 32000 },
                { cycle: 'semiannual', label: 'Semi-Annual (Every 6 Months)', price: 60000 },
                { cycle: 'annual', label: 'Annual Cloud Sync', price: 110000 },
            ],
            details: 'Automated encrypted snapshot synchronization to high-availability PostgreSQL/S3 cluster with quarterly disaster recovery verification.',
        },
        locationTerms: [
            'On-site deployment engineer travel and logistics apply for installations outside state capital / metropolitan centers.',
            'Minimum recommended hardware: Quad-Core mini-PC / server, 16GB RAM, 512GB SSD, and uninterrupted power supply (UPS) battery backup.',
            'Deployment package includes comprehensive cashier, supervisor, and inventory management staff onboarding.',
            'Includes 1 year complimentary software patches, security fixes, and hardware diagnostic advisory.',
        ],
    };

    const copyToClipboard = (text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const toggleFeature = (id: string) => {
        setSelectedFeatures((prev) =>
            prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
        );
    };

    // Calculate Custom Features Total
    const customMonthlyTotal = useMemo(() => {
        const featuresFee = selectedFeatures.reduce((acc, featId) => {
            const item = featurePricing.find((f) => f.id === featId);
            return acc + (item?.monthlyPrice || 0);
        }, 0);
        return BASE_PLATFORM_FEE + featuresFee;
    }, [selectedFeatures, featurePricing]);

    const customYearlyTotal = useMemo(() => {
        // 17% annual discount (pay for 10 months)
        return customMonthlyTotal * 10;
    }, [customMonthlyTotal]);

    // Calculate Local Installation Total
    const activeBackupCycle = localInstallation.withBackup.cycles.find(
        (c) => c.cycle === selectedBackupCycle
    );

    return (
        <div className="min-h-screen bg-slate-50 text-slate-800 py-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                {/* Top Navigation */}
                <div className="flex items-center justify-between mb-8">
                    <Link
                        to={
                            tenantId
                                ? `/verify-subscription?tenantId=${tenantId}&email=${encodeURIComponent(emailParam)}`
                                : '/login'
                        }
                        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700 transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Back to Activation
                    </Link>
                    <Link
                        to={`/verify-subscription?tenantId=${tenantId}&email=${encodeURIComponent(emailParam)}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border border-slate-200 text-xs font-bold text-emerald-700 shadow-sm hover:border-emerald-300 hover:shadow transition-all"
                    >
                        <ShieldCheck size={14} className="text-emerald-600" />
                        Have an Activation Code? Enter Code →
                    </Link>
                </div>

                {/* Hero Header */}
                <div className="text-center max-w-3xl mx-auto mb-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100/80 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-3">
                        <Sparkles size={14} className="text-emerald-600" />
                        Flexible Enterprise Pricing
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                        EtiPOS Subscription Packages
                    </h1>
                    <p className="mt-2 text-sm sm:text-base text-slate-600">
                        Select a standard plan, configure custom feature-based pricing, or explore local on-premise installation options.
                    </p>

                    {/* Mode Navigation Tabs */}
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-2 p-1.5 bg-slate-200/60 rounded-2xl max-w-md mx-auto">
                        <button
                            type="button"
                            onClick={() => setActiveTab('standard')}
                            className={`flex-1 py-2 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                                activeTab === 'standard'
                                    ? 'bg-white text-emerald-800 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            Standard Plans
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('custom')}
                            className={`flex-1 py-2 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                                activeTab === 'custom'
                                    ? 'bg-white text-emerald-800 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            Custom Feature-Based
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('local')}
                            className={`flex-1 py-2 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                                activeTab === 'local'
                                    ? 'bg-white text-emerald-800 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            Local Installation
                        </button>
                    </div>

                    {/* Billing Cycle Switcher (For Standard & Custom) */}
                    {activeTab !== 'local' && (
                        <div className="mt-5 flex items-center justify-center gap-3 text-xs font-semibold">
                            <span className={billingCycle === 'monthly' ? 'text-slate-900' : 'text-slate-500'}>
                                Monthly Billing
                            </span>
                            <button
                                type="button"
                                onClick={() => setBillingCycle((c) => (c === 'monthly' ? 'yearly' : 'monthly'))}
                                className={`w-12 h-6 rounded-full transition-colors p-0.5 relative ${
                                    billingCycle === 'yearly' ? 'bg-emerald-600' : 'bg-slate-300'
                                }`}
                            >
                                <motion.div
                                    layout
                                    className="w-5 h-5 rounded-full bg-white shadow-md"
                                    animate={{ x: billingCycle === 'yearly' ? 24 : 0 }}
                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                />
                            </button>
                            <span className={billingCycle === 'yearly' ? 'text-slate-900 font-bold' : 'text-slate-500'}>
                                Annual Billing
                            </span>
                            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                                Save 17% (2 Months Free)
                            </span>
                        </div>
                    )}
                </div>

                {/* TAB 1: STANDARD PACKAGES */}
                {activeTab === 'standard' && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="grid md:grid-cols-2 gap-8 mb-12"
                    >
                        {standardPlans.map((plan) => {
                            const isYearly = billingCycle === 'yearly';
                            const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
                            const periodText = isYearly ? '/ year' : '/ month';

                            return (
                                <div
                                    key={plan.id}
                                    className={`relative bg-white rounded-3xl border p-8 flex flex-col justify-between transition-all duration-300 ${
                                        plan.isPopular
                                            ? 'border-emerald-500 ring-2 ring-emerald-500/20 shadow-xl shadow-emerald-500/10'
                                            : 'border-slate-200 shadow-md hover:border-slate-300'
                                    }`}
                                >
                                    {plan.isPopular && (
                                        <div className="absolute -top-3.5 right-6 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-[11px] font-bold shadow-md">
                                            Most Popular Choice
                                        </div>
                                    )}

                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-2xl">
                                                {plan.businessModel === 'single' ? '🏪' : '🏢'}
                                            </span>
                                            <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                                        </div>
                                        <p className="text-xs text-slate-500 mb-6 min-h-[36px]">
                                            {plan.tagline}
                                        </p>

                                        {/* Price display */}
                                        <div className="flex items-baseline gap-1.5 pb-6 border-b border-slate-100 mb-6">
                                            <span className="text-4xl font-extrabold text-slate-900 tracking-tight">
                                                ₦{price.toLocaleString()}
                                            </span>
                                            <span className="text-xs font-semibold text-slate-500">
                                                {periodText}
                                            </span>
                                        </div>

                                        {/* Feature Checklist */}
                                        <div className="space-y-3 mb-8">
                                            <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                                                What's included:
                                            </p>
                                            {plan.features.map((feat, idx) => (
                                                <div key={idx} className="flex items-start gap-2.5 text-xs text-slate-700">
                                                    <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                                                    <span>{feat}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Action Button: Scroll down to Bank details */}
                                    <a
                                        href="#payment-details"
                                        className={`w-full py-3.5 rounded-xl font-bold text-xs text-center block transition-all shadow-sm ${
                                            plan.isPopular
                                                ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20'
                                                : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
                                        }`}
                                    >
                                        Select {plan.name} & View Bank Details ↓
                                    </a>
                                </div>
                            );
                        })}
                    </motion.div>
                )}

                {/* TAB 2: CUSTOM FEATURE-BASED CALCULATOR */}
                {activeTab === 'custom' && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="grid lg:grid-cols-3 gap-8 mb-12"
                    >
                        {/* Features Selection List */}
                        <div className="lg:col-span-2 space-y-4">
                            <div className="p-4 rounded-2xl bg-emerald-50/80 border border-emerald-100 flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                    <Sliders size={18} className="text-emerald-700" />
                                    <div>
                                        <p className="text-xs font-bold text-emerald-900">
                                            Tailor EtiPOS To Your Exact Requirements
                                        </p>
                                        <p className="text-[11px] text-emerald-700">
                                            Check or uncheck individual modules. Your subscription total calculates automatically.
                                        </p>
                                    </div>
                                </div>
                                <span className="text-xs font-mono font-bold text-emerald-800 bg-white px-2.5 py-1 rounded-lg border border-emerald-200">
                                    Base: ₦{BASE_PLATFORM_FEE.toLocaleString()}/mo
                                </span>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-3.5">
                                {featurePricing.map((feat) => {
                                    const isSelected = selectedFeatures.includes(feat.id);
                                    return (
                                        <div
                                            key={feat.id}
                                            onClick={() => toggleFeature(feat.id)}
                                            className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                                                isSelected
                                                    ? 'bg-white border-emerald-600 shadow-md shadow-emerald-500/5'
                                                    : 'bg-slate-50/80 border-slate-200 hover:border-slate-300'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div
                                                        className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${
                                                            isSelected ? 'bg-emerald-600 text-white' : 'border border-slate-300 bg-white'
                                                        }`}
                                                    >
                                                        {isSelected && <Check size={12} strokeWidth={3} />}
                                                    </div>
                                                    <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                                                        {feat.category}
                                                    </span>
                                                </div>
                                                <span className="text-xs font-bold text-emerald-800 font-mono">
                                                    +₦{feat.monthlyPrice.toLocaleString()}/mo
                                                </span>
                                            </div>
                                            <h4 className="text-sm font-bold text-slate-900 leading-snug">
                                                {feat.name}
                                            </h4>
                                            <p className="text-[11px] text-slate-500 mt-1 leading-normal">
                                                {feat.description}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Live Quote Summary Card */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-xl sticky top-8">
                                <div className="flex items-center gap-2 pb-3 border-b border-slate-100 mb-4">
                                    <Sparkles size={18} className="text-emerald-600" />
                                    <h3 className="text-base font-bold text-slate-900">Custom Plan Quote</h3>
                                </div>

                                <div className="space-y-2 text-xs mb-6">
                                    <div className="flex justify-between py-1 text-slate-600">
                                        <span>Base Platform Hosting & Cloud DB</span>
                                        <span className="font-mono font-semibold">₦{BASE_PLATFORM_FEE.toLocaleString()}</span>
                                    </div>
                                    {selectedFeatures.map((id) => {
                                        const feat = featurePricing.find((f) => f.id === id);
                                        return (
                                            <div key={id} className="flex justify-between py-1 text-slate-700">
                                                <span className="truncate pr-2">{feat?.name}</span>
                                                <span className="font-mono font-semibold text-emerald-700">
                                                    +₦{feat?.monthlyPrice.toLocaleString()}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="pt-4 border-t border-slate-100 mb-6">
                                    <div className="flex justify-between items-baseline mb-1">
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                            {billingCycle === 'yearly' ? 'Annual Total' : 'Monthly Total'}
                                        </span>
                                        <span className="text-2xl font-extrabold text-slate-900">
                                            ₦{billingCycle === 'yearly' ? customYearlyTotal.toLocaleString() : customMonthlyTotal.toLocaleString()}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-slate-500">
                                        {billingCycle === 'yearly'
                                            ? 'Annual prepay includes 2 months free discount.'
                                            : 'Billed on recurring monthly basis.'}
                                    </p>
                                </div>

                                <a
                                    href="#payment-details"
                                    className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs text-center block shadow-md shadow-emerald-600/20 transition-all"
                                >
                                    Proceed To Payment & Activation ↓
                                </a>

                                <p className="text-[10px] text-slate-400 text-center mt-3">
                                    Include your selected feature list in your proof of payment email.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* TAB 3: LOCAL INSTALLATION PLANS */}
                {activeTab === 'local' && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-8 mb-12"
                    >
                        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3 text-xs text-amber-900">
                            <Info size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-bold">On-Premise Local Server Deployment</p>
                                <p className="mt-0.5 text-amber-800">
                                    Engineered for businesses requiring complete offline autonomy, high-throughput LAN setups, or locations with intermittent Internet connectivity.
                                </p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            {/* Option 1: Without Cloud Backup */}
                            <div
                                onClick={() => setLocalMode('without_backup')}
                                className={`p-8 rounded-3xl border-2 cursor-pointer transition-all ${
                                    localMode === 'without_backup'
                                        ? 'bg-white border-emerald-600 shadow-xl ring-2 ring-emerald-500/20'
                                        : 'bg-white/80 border-slate-200 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                                        <HardDrive size={20} className="text-slate-700" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-900">Local (Without Cloud Backup)</h3>
                                        <span className="text-[11px] text-slate-500">Standalone offline server setup</span>
                                    </div>
                                </div>

                                <div className="my-6 pb-6 border-b border-slate-100">
                                    <span className="text-3xl font-extrabold text-slate-900">
                                        ₦{localInstallation.withoutBackup.price.toLocaleString()}
                                    </span>
                                    <span className="text-xs text-slate-500 font-semibold"> (One-time deployment)</span>
                                </div>

                                <p className="text-xs text-slate-600 leading-relaxed mb-6">
                                    {localInstallation.withoutBackup.details}
                                </p>

                                <ul className="space-y-2 text-xs text-slate-700 mb-6">
                                    <li className="flex items-center gap-2">
                                        <CheckCircle2 size={14} className="text-emerald-600" />
                                        Complete on-site PostgreSQL server configuration
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <CheckCircle2 size={14} className="text-emerald-600" />
                                        Unlimited LAN offline POS terminals
                                    </li>
                                    <li className="flex items-center gap-2 text-amber-700 font-medium">
                                        <HelpCircle size={14} className="text-amber-600" />
                                        Client takes full responsibility for hardware storage & drive backups
                                    </li>
                                </ul>

                                <a
                                    href="#payment-details"
                                    className="w-full py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs text-center block transition-all"
                                >
                                    Select Standalone Local Setup ↓
                                </a>
                            </div>

                            {/* Option 2: With Automated Cloud Backup */}
                            <div
                                onClick={() => setLocalMode('with_backup')}
                                className={`p-8 rounded-3xl border-2 cursor-pointer transition-all ${
                                    localMode === 'with_backup'
                                        ? 'bg-white border-emerald-600 shadow-xl ring-2 ring-emerald-500/20'
                                        : 'bg-white/80 border-slate-200 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                                        <Server size={20} className="text-emerald-700" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-lg font-bold text-slate-900">Local + Automated Cloud Backup</h3>
                                            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                                                Recommended
                                            </span>
                                        </div>
                                        <span className="text-[11px] text-slate-500">Best of both worlds: offline speed + cloud peace of mind</span>
                                    </div>
                                </div>

                                <div className="my-6 pb-6 border-b border-slate-100">
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-3xl font-extrabold text-slate-900">
                                            ₦{localInstallation.withBackup.setupPrice.toLocaleString()}
                                        </span>
                                        <span className="text-xs text-slate-500 font-semibold">one-time setup + </span>
                                        <span className="text-xl font-bold text-emerald-700">
                                            ₦{activeBackupCycle?.price.toLocaleString()}
                                        </span>
                                        <span className="text-xs text-emerald-700 font-semibold">
                                            /{activeBackupCycle?.cycle}
                                        </span>
                                    </div>
                                </div>

                                {/* Backup Frequency Selector */}
                                <div className="mb-6">
                                    <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">
                                        Select Cloud Backup Billing Frequency:
                                    </label>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {localInstallation.withBackup.cycles.map((c) => (
                                            <button
                                                key={c.cycle}
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedBackupCycle(c.cycle);
                                                }}
                                                className={`p-2.5 rounded-xl border text-left transition-all ${
                                                    selectedBackupCycle === c.cycle
                                                        ? 'bg-emerald-50 border-emerald-600 text-emerald-900 ring-1 ring-emerald-500/20'
                                                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300'
                                                }`}
                                            >
                                                <p className="text-[11px] font-bold">{c.label}</p>
                                                <p className="text-xs font-extrabold mt-0.5 font-mono">
                                                    ₦{c.price.toLocaleString()}
                                                </p>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <p className="text-xs text-slate-600 leading-relaxed mb-6">
                                    {localInstallation.withBackup.details}
                                </p>

                                <a
                                    href="#payment-details"
                                    className="w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs text-center block shadow-md shadow-emerald-600/20 transition-all"
                                >
                                    Select Local + Cloud Backup Plan ↓
                                </a>
                            </div>
                        </div>

                        {/* Location-Based Terms Card */}
                        <div className="bg-white rounded-3xl border border-slate-200 p-6">
                            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                                <Building2 size={16} className="text-emerald-600" />
                                On-Premise Installation Terms & Technical SLA
                            </h4>
                            <div className="grid sm:grid-cols-2 gap-3 text-xs text-slate-600">
                                {localInstallation.locationTerms.map((term, idx) => (
                                    <div key={idx} className="flex items-start gap-2">
                                        <ChevronRight size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                                        <span>{term}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* SECTION 4: PAYMENT DETAILS & PROOF INSTRUCTIONS */}
                <div id="payment-details" className="pt-6">
                    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 sm:p-10 text-white shadow-2xl relative overflow-hidden">
                        {/* Glowing Background Orbs */}
                        <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
                        <div className="absolute bottom-0 left-0 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

                        <div className="relative z-10 max-w-4xl mx-auto">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-700/80 mb-8">
                                <div>
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold mb-2">
                                        <CreditCard size={14} /> Official Company Payment Account
                                    </div>
                                    <h2 className="text-2xl font-extrabold text-white">
                                        Bank Transfer & Activation Instructions
                                    </h2>
                                </div>
                                <div className="text-left sm:text-right">
                                    <span className="text-xs text-slate-400 block">Need urgent verification?</span>
                                    <a
                                        href={`tel:${bankDetails.supportPhone}`}
                                        className="text-emerald-400 hover:text-emerald-300 text-sm font-bold inline-flex items-center gap-1.5"
                                    >
                                        <Phone size={14} /> {bankDetails.supportPhone}
                                    </a>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6 mb-8">
                                {/* Bank Card */}
                                <div className="bg-slate-800/80 backdrop-blur-md rounded-2xl border border-slate-700 p-6 space-y-4">
                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                        Direct Bank Deposit / Transfer
                                    </p>

                                    <div>
                                        <span className="text-xs text-slate-400">Bank Name</span>
                                        <p className="text-lg font-bold text-white">{bankDetails.bankName}</p>
                                    </div>

                                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/80 border border-slate-700">
                                        <div>
                                            <span className="text-[11px] text-slate-400">Account Number</span>
                                            <p className="text-xl font-mono font-bold text-emerald-400 tracking-wider">
                                                {bankDetails.accountNumber}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => copyToClipboard(bankDetails.accountNumber, 'accountNumber')}
                                            className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 inline-flex items-center gap-1.5 border border-slate-600 transition-colors"
                                        >
                                            {copiedField === 'accountNumber' ? (
                                                <>
                                                    <Check size={14} className="text-emerald-400" /> Copied!
                                                </>
                                            ) : (
                                                <>
                                                    <Copy size={14} /> Copy
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    <div>
                                        <span className="text-xs text-slate-400">Account Name</span>
                                        <p className="text-sm font-bold text-white">{bankDetails.accountName}</p>
                                    </div>

                                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-200">
                                        <span className="font-bold">Payment Narration / Reference: </span>
                                        <span>Use your business name or URL slug as reference.</span>
                                    </div>
                                </div>

                                {/* Proof Submission Card */}
                                <div className="bg-slate-800/80 backdrop-blur-md rounded-2xl border border-slate-700 p-6 flex flex-col justify-between">
                                    <div>
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                                            Send Proof of Payment
                                        </p>
                                        <p className="text-xs text-slate-300 leading-relaxed mb-4">
                                            Once payment is made, send your transaction receipt or screenshot to our billing team. Your 6-digit subscription PIN will be dispatched immediately via email.
                                        </p>

                                        <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/80 border border-slate-700 mb-4">
                                            <div className="min-w-0 pr-2">
                                                <span className="text-[11px] text-slate-400">Billing Email</span>
                                                <p className="text-sm font-bold text-emerald-400 truncate">
                                                    {bankDetails.proofEmail}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => copyToClipboard(bankDetails.proofEmail, 'proofEmail')}
                                                className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 inline-flex items-center gap-1.5 border border-slate-600 transition-colors flex-shrink-0"
                                            >
                                                {copiedField === 'proofEmail' ? (
                                                    <>
                                                        <Check size={14} className="text-emerald-400" /> Copied!
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy size={14} /> Copy
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-3 pt-2">
                                        <a
                                            href={`mailto:${bankDetails.proofEmail}?subject=Proof of Payment - ${encodeURIComponent(tenantId || 'Subscription')}&body=Hello EtiPOS Team,%0D%0A%0D%0APlease find attached our proof of payment for EtiPOS subscription.%0D%0A%0D%0ACompany Name: %0D%0ATenant ID: ${tenantId}%0D%0AEmail: ${emailParam}`}
                                            className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs text-center inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/30 transition-all"
                                        >
                                            <Mail size={16} /> Open Email App to Send Proof
                                        </a>

                                        <Link
                                            to={`/verify-subscription?tenantId=${tenantId}&email=${encodeURIComponent(emailParam)}`}
                                            className="w-full py-2.5 rounded-xl bg-slate-700/60 hover:bg-slate-700 text-slate-200 font-semibold text-xs text-center block transition-colors"
                                        >
                                            I Already Have My 6-Digit Code → Enter Code
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                                {bankDetails.paymentInstructions}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
